<script setup>
import InputError from "@/Components/InputError.vue";
import InputLabel from "@/Components/InputLabel.vue";
import { useForm, usePage } from "@inertiajs/inertia-vue3";
import { computed, ref, watch } from "vue";
import TextInput from "@/Components/TextInput.vue";
import dayjs from "dayjs";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import '@vuepic/vue-datepicker/dist/main.css'
import Datepicker from "@vuepic/vue-datepicker";
import { Inertia } from "@inertiajs/inertia";
import { useHelpers } from "@/helpers";
import NumberInput from "@/Components/NumberInput.vue";
import SearchSelect from "@/Components/SearchSelect.vue";

const currentUser = usePage().props.value.auth.user;
const helpers = useHelpers();
const projectProperty = computed(() => usePage().props.value.project);
let project = projectProperty.value;
watch(projectProperty, (value) => {
  project = projectProperty.value;
});

const props = defineProps({
  vendors: Array,
  hideSave: {
    type: Boolean,
    default: () => false,
  },
});


const minDate = dayjs().toDate();
const depositSent = ref(null);
const form = useForm({
  _method: 'PUT',
  vendor: {
    vid: null,
    vendor_po: null,
    confirm_num: null,
    sent_date: null,
  }
});

const saving = ref(false);
const addVendor = () => {
  if (depositSent.value) {
    form.vendor.sent_date = depositSent.value;
  }
  form.post(route('admin.projects.update', { project: project.pid }), {
    preserveScroll: true,
    onSuccess() {
      form.reset();
    },
    onFinish() {
      saving.value = false;
    }
  });
  saving.value = false;
}

const removeVendor = (vendorId) => {
  Inertia.post(route('admin.projects.vendors.destroy', {
    projectid: project.pid,
    project_vendorid: vendorId
  }), { _method: 'DELETE' }, {
    preserveScroll: true
  });
}

const vendorOptions = computed(() =>
  (props.vendors || [])
    .filter((vendor) => vendor?.vendorName && vendor?.vendorID != null)
    .map((vendor) => ({
      label: vendor.vendorName,
      value: Number(vendor.vendorID),
    }))
);

const vendorModel = computed({
  get: () => (form.vendor.vid != null ? Number(form.vendor.vid) : null),
  set: (v) => {
    form.vendor.vid = v == null ? null : Number(v);
  },
});
</script>

<template>
  <div class="px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md mb-5">
    <table class="min-w-full w-full text-left text-sm font-light" v-if="project.vendors.length > 0">
      <thead class="border-b font-medium dark:border-neutral-500">
        <tr>
          <th scope="col" class="px-4 py-2">Vendor Name</th>
          <th scope="col" class="px-4 py-2">Vendor PO</th>
          <th scope="col" class="px-4 py-2">Confirmation Number</th>
          <th scope="col" class="px-4 py-2">Deposit Sent</th>
          <th scope="col" class="px-4 py-2">Action</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(vendor, index) in project.vendors"
          class="border-b transition duration-300 ease-in-out hover:bg-neutral-200">
          <td class="px-4 py-2 break-words" v-text="vendor.vendor.vendorName"></td>
          <td class="px-4 py-2 break-words" v-text="vendor.vendor_po"></td>
          <td class="px-4 py-2 break-words" v-text="vendor.confirm_num"></td>
          <td class="px-4 py-2 break-words" v-text="vendor.sent_date"></td>
          <td class="px-4 py-2 break-words">
            <span class="fa fa-trash cursor-pointer" @click="removeVendor(vendor.tbl_vendorid)"
              v-if="!hideSave && helpers.userCanManageProject(currentUser)"></span>
          </td>
        </tr>
      </tbody>
    </table>
    <p v-if="project.vendors.length === 0">No records...</p>
  </div>

  <div class="px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md"
    v-if="!hideSave && helpers.userCanManageProject(currentUser)">
    <div class="sm:grid grid-cols-6 gap-6">
      <div class="col-span-6 sm:col-span-4">
        <!-- Vendor -->
        <InputLabel for="vendor" value="Vendor Name" :required="true" />
        <SearchSelect v-model="vendorModel" :options="vendorOptions" placeholder="Please select…" :disabled="saving" />
        <InputError class="mt-2" :message="form.errors['vendor.vid']" />
      </div>
      <div class="col-span-6 sm:col-span-4">
        <!-- Vendor PO -->
        <InputLabel for="vendor-po" value="Vendor PO" />
        <TextInput id="vendor-po" type="text" class="mt-1 block w-full" autocomplete="vendor-po"
          v-model="form.vendor.vendor_po" />
        <InputError class="mt-2" :message="form.errors['vendor.vendor_po']" />
      </div>
      <div class="col-span-6 sm:col-span-4">
        <!-- Confirmation Number -->
        <InputLabel for="confirmation-number" value="Confirmation Number" />
        <NumberInput id="confirmation-number" type="number" class="mt-1 block w-full" autocomplete="project-name"
          v-model="form.vendor.confirm_num" />
        <InputError class="mt-2" :message="form.errors['vendor.confirm_num']" />
      </div>
      <div class="col-span-6 sm:col-span-4">
        <!-- Deposit Sent -->
        <InputLabel for="deposit-sent" value="Deposit Sent" />
        <Datepicker class="mt-1 block w-full disabled:bg-gray-50" v-model="depositSent" format="MMMM d, yyyy"
          :min-date="minDate" model-type="yyyy-MM-dd" auto-apply :close-on-auto-apply="true"
          :enable-time-picker="false" />
        <InputError class="mt-2" :message="form.errors['vendor.sent_date']" />
      </div>
    </div>
  </div>
  <div
    class="flex items-center justify-center px-4 py-3 bg-gray-50 text-right sm:px-6 shadow sm:rounded-bl-md sm:rounded-br-md sticky bottom-0">
    <PrimaryButton @click.prevent="addVendor()" :disabled="saving" v-if="hideSave === false">Save</PrimaryButton>
  </div>
</template>
